import { useEffect, useRef, useState } from "react";
import { useAutosave } from "../hooks/useAutosave";
import {
  ActionIcon, Anchor, Autocomplete, Avatar, Badge, Button, Card, CloseButton, Divider, Drawer, Flex, Group,
  Pill, PillsInput, SegmentedControl, Select, Stack, Text, Textarea, TextInput, Title, Tooltip,
} from "@mantine/core";
import { useIsMobile } from "../hooks/useIsMobile";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconCheck, IconExternalLink, IconPencil, IconRobot, IconSparkles,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  approveSection, generateRhpDraft, getCandidate, getProjectCandidate,
  searchCandidates, updateProject, updateProjectCandidate,
} from "../api";
import { useStore } from "../store";
import { formatCandidateName } from "../_Shared/candidateName";
import { alphaDisplayName } from "../_Shared/candidateAlphaSort";
import { candidateHeadline, candidateRoleAndCompany } from "../_Shared/candidateRole";
import CompanyLink from "../components/CompanyLink";

// Stage/ranking options are localized inside the components (t() isn't in module scope),
// so these carry translation keys / raw values, not display labels.
const stageKeys = [
  { value: "identified", tKey: "stage_identified" },
  { value: "longlist", tKey: "stage_longlist" },
  { value: "shortlist", tKey: "stage_shortlist" },
  { value: "interview", tKey: "stage_interview" },
  { value: "finalist", tKey: "stage_finalist" },
  { value: "placed", tKey: "stage_placed" },
  { value: "rejected", tKey: "stage_rejected" },
];

const rankingValues = ["A", "B", "C"];
const scoreOptions = [
  { value: "", label: "-" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

// The RHP draft flow over-escapes newlines, so legacy rows can hold a literal "\n"
// (backslash+n) instead of a real break. New drafts are normalized server-side; this
// heals what's already stored for display without a data migration.
const normalizeNewlines = (v) => (v ?? "").replace(/\\r\\n|\\n/g, "\n").replace(/\\t/g, "\t");

export default function ProjectCandidateDrawer({
  opened, onClose, projectCandidate: pc, candidate, project, onUpdate, onProjectUpdate, onNavigate,
}) {
  const isMobile = useIsMobile();
  if (!pc || !candidate) return null;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      // Full-width sheet on a phone (a fixed-width box would be clamped and cramped);
      // xl (~780px) on desktop so the side-by-side Anforderung/Realität columns in the
      // competency cards each get enough width for long requirement text to breathe.
      size={isMobile ? "100%" : "xl"}
      title={null}
      withCloseButton={false}
    >
      <DrawerBody
        pc={pc} candidate={candidate} project={project}
        onUpdate={onUpdate} onProjectUpdate={onProjectUpdate}
        onNavigate={onNavigate} onClose={onClose}
      />
    </Drawer>
  );
}

function DrawerBody({ pc, candidate, project, onUpdate, onProjectUpdate, onNavigate, onClose }) {
  useDraftStatusPoll(pc, project, onUpdate);

  return (
    <Stack gap="lg">
      <DrawerHeader
        pc={pc} candidate={candidate} project={project}
        onUpdate={onUpdate} onNavigate={onNavigate} onClose={onClose}
      />
      {pc.stage === "rejected" && (
        <RejectionInfoSection pc={pc} project={project} onUpdate={onUpdate} />
      )}
      <Divider />
      <RhpDraftBar
        pc={pc} project={project} onUpdate={onUpdate}
        scope="longlistCopy" hintKey="draft_longlist_hint"
      />
      <TextSection
        labelKey="executive_summary"
        value={pc.executiveSummary}
        field="executiveSummary"
        provenance={pc.executiveSummaryProvenance}
        pc={pc} project={project} onUpdate={onUpdate}
        placeholderKey="executive_summary_placeholder"
        minRows={4}
      />
      <AnnotationsSection pc={pc} project={project} onUpdate={onUpdate} />
      <ProcessingRemarksSection pc={pc} project={project} onUpdate={onUpdate} />
      <Divider />
      <RhpDraftBar
        pc={pc} project={project} onUpdate={onUpdate}
        scope="assessmentCopy" hintKey="draft_assessment_hint"
      />
      <CompetencyAssessmentMatrix
        pc={pc} project={project} onUpdate={onUpdate} onProjectUpdate={onProjectUpdate}
      />
      <TextSection
        labelKey="personality_notes"
        value={pc.personalityNotes}
        field="personalityNotes"
        provenance={pc.personalityNotesProvenance}
        pc={pc} project={project} onUpdate={onUpdate}
        placeholderKey="personality_notes_placeholder"
        minRows={3}
      />
      <Divider />
      <ReferrersSection
        pc={pc} project={project} onUpdate={onUpdate}
        onNavigate={onNavigate} onClose={onClose}
      />
    </Stack>
  );
}

// ── Header ───────────────────────────────────────────────────────────────

function DrawerHeader({ pc, candidate, project, onUpdate, onNavigate, onClose }) {
  const { t } = useTranslation("Veft");

  // Same pair as the profile header / pipeline card, with the company clickable
  // when candidateRoleAndCompany resolved an id that belongs to the displayed name.
  const { role, company, companyId, isFormer } = candidateRoleAndCompany(candidate);
  const roleLabel = isFormer && role ? `${t("role_former_prefix")} ${role}` : role;

  const sourceOptions = [
    { value: "", label: "—" },
    { value: "direct_approach", label: t("source_direct_approach") },
    { value: "referral", label: t("source_referral") },
    { value: "application", label: t("source_application") },
    { value: "database", label: t("source_database") },
    { value: "linkedin_recruiter", label: t("source_linkedin_recruiter") },
  ];

  async function handleChange(field, value) {
    try {
      const updated = await updateProjectCandidate(project.id, pc.id, { [field]: value });
      onUpdate(updated);
      notifications.show({ message: t("saved"), color: "green", autoClose: 1500 });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("update_failed") });
    }
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group>
          <Avatar size="md" radius="xl" color="blue">
            {candidate.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
          </Avatar>
          <div>
            <Text size="lg" fw={600}>{alphaDisplayName(candidate)}</Text>
            {(role || company) && (
              <Text size="sm" c="dimmed">
                {roleLabel}
                {roleLabel && company ? " · " : ""}
                <CompanyLink company={company} companyId={companyId} />
              </Text>
            )}
          </div>
        </Group>
        <Group gap="xs">
          <Button
            size="xs" variant="subtle" leftSection={<IconExternalLink size={14} />}
            onClick={() => { onClose(); onNavigate(pc.candidateId); }}
          >
            {t("view_profile")}
          </Button>
          <CloseButton onClick={onClose} />
        </Group>
      </Group>
      {/* Stack the three selects on a phone so none is crushed to an unusable width; inline on desktop. */}
      <Flex gap="sm" direction={{ base: "column", xs: "row" }} align={{ base: "stretch", xs: "flex-end" }}>
        <Select
          size="xs" label={t("source_label")} value={pc.source || ""} data={sourceOptions}
          onChange={(val) => handleChange("source", val || null)} style={{ flex: 1 }}
        />
        <Select
          size="xs" label={t("stage_label")} value={pc.stage}
          data={stageKeys.map((s) => ({ value: s.value, label: t(s.tKey) }))}
          onChange={(val) => handleChange("stage", val)} style={{ flex: 1 }}
        />
        <Select
          size="xs" label={t("ranking_label")} value={pc.ranking || ""}
          data={[{ value: "", label: t("no_ranking") }, ...rankingValues.map((v) => ({ value: v, label: v }))]}
          onChange={(val) => handleChange("ranking", val ?? "")} w={{ base: "100%", xs: 100 }}
        />
      </Flex>
    </Stack>
  );
}

// ── Rejection Info ───────────────────────────────────────────────────────

function RejectionInfoSection({ pc, project, onUpdate }) {
  const { t } = useTranslation("Veft");
  const [reason, setReason] = useState(pc.rejectionReason ?? "");
  const [saving, setSaving] = useState(false);
  const [savingInitiator, setSavingInitiator] = useState(false);
  const isDirty = reason !== (pc.rejectionReason ?? "");
  useEffect(() => { setReason(pc.rejectionReason ?? ""); }, [pc.rejectionReason]);

  // Who-rejected is a structured field the backend accepts on an already-rejected
  // candidate (ProjectsController.UpdateCandidate). The Kanban X dialog always sets
  // it; candidates rejected via the drawer's stage picker land here with it null, so
  // this selector is the one place to record/repair it.
  async function saveInitiator(value) {
    if (!value || value === pc.rejectedBy) return;
    setSavingInitiator(true);
    try {
      const result = await updateProjectCandidate(project.id, pc.id, { rejectedBy: value });
      onUpdate(result);
      notifications.show({ message: t("saved"), color: "green", autoClose: 1500 });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("update_failed") });
    } finally {
      setSavingInitiator(false);
    }
  }

  async function saveReason(quiet = false) {
    if (saving) return;
    if (reason === (pc.rejectionReason ?? "")) return;
    setSaving(true);
    try {
      const result = await updateProjectCandidate(project.id, pc.id, { rejectionReason: reason });
      onUpdate(result);
      if (!quiet) notifications.show({ title: t("saved"), message: t("rejection_reason_saved"), color: "green" });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("reason_save_failed") });
    } finally {
      setSaving(false);
    }
  }

  useAutosave({ value: reason, isDirty, save: () => saveReason(true) });

  async function handleRestore() {
    const restoreStage = pc.rejectedAtStage || "identified";
    try {
      const result = await updateProjectCandidate(project.id, pc.id, { stage: restoreStage });
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("restore_failed") });
    }
  }

  return (
    <Card withBorder p="sm" style={{ borderColor: "var(--mantine-color-red-4)", backgroundColor: "var(--mantine-color-red-0)" }}>
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="sm" fw={600} c="red">{t("rejected")}</Text>
          <Button size="xs" variant="light" onClick={handleRestore}>
            {t("restore_to_pipeline")}
          </Button>
        </Group>
        <Group gap="sm">
          {pc.rejectedAtStage && (
            <Badge size="sm" variant="light" color="gray">
              {t("last_stage", { stage: t(`stage_${pc.rejectedAtStage}`) })}
            </Badge>
          )}
          {pc.rejectedAt && (
            <Text size="xs" c="dimmed">{new Date(pc.rejectedAt).toLocaleDateString()}</Text>
          )}
        </Group>
        <Select
          size="xs"
          label={t("reject_who_initiated")}
          placeholder={t("reject_initiator_placeholder")}
          data={[
            { value: "candidate", label: t("rejection_candidate") },
            { value: "consultant", label: t("rejection_consultant") },
            { value: "client", label: t("rejection_client") },
          ]}
          value={pc.rejectedBy || null}
          onChange={saveInitiator}
          disabled={savingInitiator}
          w={260}
        />
        <Textarea
          size="xs"
          label={t("rejection_reason_label")}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          onBlur={() => { if (isDirty) saveReason(true); }}
          rows={2}
          placeholder={t("rejection_reason_placeholder")}
        />
        {isDirty && (
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={() => setReason(pc.rejectionReason ?? "")}>{t("discard")}</Button>
            <Button size="xs" onClick={saveReason} loading={saving}>{t("save")}</Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

// ── Provenance pill + approve button ─────────────────────────────────────

const provenanceMeta = {
  aiGenerated: { color: "violet", icon: IconSparkles, key: "provenance_ai_generated" },
  humanEdited: { color: "yellow", icon: IconPencil, key: "provenance_human_edited" },
  humanWritten: { color: "blue", icon: IconPencil, key: "provenance_human_written" },
  humanApproved: { color: "green", icon: IconCheck, key: "provenance_human_approved" },
};

function ProvenancePill({ value }) {
  const { t } = useTranslation("Veft");
  if (!value || value === "none") return null;
  const meta = provenanceMeta[value];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <Badge size="xs" variant="light" color={meta.color} leftSection={<Icon size={10} />}>
      {t(meta.key)}
    </Badge>
  );
}

function ApproveButton({ provenance, onApprove, disabled }) {
  const { t } = useTranslation("Veft");
  const isApproved = provenance === "humanApproved";
  if (isApproved) return null;
  return (
    <Button
      size="compact-xs" variant="light" color="green"
      leftSection={<IconCheck size={12} />}
      onClick={onApprove} disabled={disabled}
    >
      {t("approve")}
    </Button>
  );
}

function tintFor(provenance) {
  if (provenance === "humanApproved") return "var(--mantine-color-green-0)";
  if (provenance === "aiGenerated") return "var(--mantine-color-violet-0)";
  if (provenance === "humanEdited") return "var(--mantine-color-yellow-0)";
  return undefined;
}

// ── Annotations ──────────────────────────────────────────────────────────

function AnnotationsSection({ pc, project, onUpdate }) {
  const { t } = useTranslation("Veft");
  const [newAnnotation, setNewAnnotation] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const annotations = pc.annotations ?? [];
  const provenance = pc.annotationsProvenance || "none";
  const verdict = pc.verdict ?? null;
  const annotationDraftDirty = !!newAnnotation.trim() || editingIdx != null;
  useEffect(() => {
    if (!annotationDraftDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [annotationDraftDirty]);

  async function save(updated) {
    setSaving(true);
    try {
      const result = await updateProjectCandidate(project.id, pc.id, { annotations: updated });
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    } finally {
      setSaving(false);
    }
  }

  function addAnnotation() {
    if (!newAnnotation.trim()) return;
    save([...annotations, newAnnotation.trim()]);
    setNewAnnotation("");
  }

  function removeAnnotation(idx) {
    if (editingIdx === idx) setEditingIdx(null);
    save(annotations.filter((_, i) => i !== idx));
  }

  function startEdit(idx) {
    setEditingIdx(idx);
    setEditDraft(annotations[idx] ?? "");
  }

  function commitEdit() {
    if (editingIdx == null) return;
    const next = editDraft.trim();
    if (!next) {
      // Empty edit clears the entry — keeps a single delete affordance instead
      // of two ways to delete that diverge in behavior.
      removeAnnotation(editingIdx);
      return;
    }
    if (next !== annotations[editingIdx]) {
      save(annotations.map((a, i) => (i === editingIdx ? next : a)));
    }
    setEditingIdx(null);
  }

  async function setVerdict(value) {
    try {
      const result = await updateProjectCandidate(project.id, pc.id, { verdict: value });
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    }
  }

  async function handleApprove() {
    try {
      const result = await approveSection(project.id, pc.id, "annotations");
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("approve_failed") });
    }
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Group gap="xs">
          <Title order={5}>{t("annotations")}</Title>
          <ProvenancePill value={provenance} />
        </Group>
        <ApproveButton provenance={provenance} onApprove={handleApprove} disabled={annotations.length === 0} />
      </Group>

      <Group gap="xs" align="center">
        <Text size="xs" c="dimmed">{t("verdict")}:</Text>
        <SegmentedControl
          size="xs"
          value={verdict ?? ""}
          onChange={setVerdict}
          data={[
            { value: "", label: t("verdict_unset") },
            { value: "go", label: t("verdict_go") },
            { value: "noGo", label: t("verdict_nogo") },
          ]}
          color={verdict === "go" ? "green" : verdict === "noGo" ? "red" : undefined}
        />
      </Group>

      <Text size="xs" c="dimmed">
        {t("annotations_help")}
      </Text>
      {annotations.map((a, i) => (
        <Card key={i} p="xs" withBorder style={{ backgroundColor: tintFor(provenance) }}>
          {editingIdx === i ? (
            <Stack gap="xs">
              <Textarea
                size="xs"
                value={editDraft}
                onChange={(e) => setEditDraft(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commitEdit(); }
                  if (e.key === "Escape") { e.preventDefault(); setEditingIdx(null); }
                }}
                autosize
                minRows={2}
                maxRows={8}
                autoFocus
              />
              <Group gap="xs">
                <Button size="xs" loading={saving} onClick={commitEdit}>{t("save")}</Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => setEditingIdx(null)}>
                  {t("cancel")}
                </Button>
              </Group>
            </Stack>
          ) : (
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Text
                size="sm"
                style={{ whiteSpace: "pre-wrap", flex: 1, cursor: "pointer" }}
                onClick={() => startEdit(i)}
              >
                {a}
              </Text>
              <Group gap={2} wrap="nowrap">
                <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => startEdit(i)}>
                  <IconPencil size={12} />
                </ActionIcon>
                <CloseButton size="sm" onClick={() => removeAnnotation(i)} />
              </Group>
            </Group>
          )}
        </Card>
      ))}
      <Group gap="xs" align="flex-start">
        <Textarea
          size="xs" placeholder={t("annotation_placeholder")} value={newAnnotation}
          onChange={(e) => setNewAnnotation(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              addAnnotation();
            }
          }}
          autosize
          minRows={2}
          maxRows={6}
          style={{ flex: 1 }}
        />
        <Button size="xs" onClick={addAnnotation} loading={saving} disabled={!newAnnotation.trim()}>
          {t("add")}
        </Button>
      </Group>
    </Stack>
  );
}

// ── Processing Remarks ───────────────────────────────────────────────────
//
// AI-generated, consultant-only remarks about the *generation run itself* —
// data quality issues, conflicting sources, ambiguities the AI noticed (e.g.
// "web search results refer to a different person"). Never shown to clients.
// Replaced wholesale on each draft generation; consultant can dismiss
// individual remarks once acknowledged.

function ProcessingRemarksSection({ pc, project, onUpdate }) {
  const { t } = useTranslation("Veft");
  const remarks = pc.processingRemarks ?? [];
  const [saving, setSaving] = useState(false);

  async function dismiss(idx) {
    setSaving(true);
    try {
      const next = remarks.filter((_, i) => i !== idx);
      const result = await updateProjectCandidate(project.id, pc.id, { processingRemarks: next });
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("dismiss_failed") });
    } finally {
      setSaving(false);
    }
  }

  if (remarks.length === 0) return null;

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Title order={5}>{t("processing_remarks")}</Title>
        <Badge size="xs" variant="light" color="orange" leftSection={<IconRobot size={10} />}>
          {t("provenance_ai_generated")}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed">{t("processing_remarks_help")}</Text>
      {remarks.map((r, i) => (
        <Card key={i} p="xs" withBorder style={{ backgroundColor: "var(--mantine-color-orange-0)" }}>
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{r}</Text>
            <CloseButton size="sm" disabled={saving} onClick={() => dismiss(i)} />
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

// ── RHP Draft Bar ────────────────────────────────────────────────────────

const draftStatusColors = { generating: "blue", complete: "green", failed: "red" };

// One poller for both buttons — they share ProjectCandidate.draftStatus.
function useDraftStatusPoll(pc, project, onUpdate) {
  const { t } = useTranslation("Veft");
  const isRunning = (pc.draftStatus || "none") === "generating";
  const pollRef = useRef(null);

  useEffect(() => {
    if (!isRunning) return;
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await getProjectCandidate(project.id, pc.id);
        if (fresh) {
          onUpdate(fresh);
          if (fresh.draftStatus !== "generating") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (fresh.draftStatus === "complete") {
              notifications.show({ title: t("draft_done"), message: t("draft_done_msg"), color: "green" });
            } else if (fresh.draftStatus === "failed") {
              notifications.show({
                title: t("draft_failed"),
                message: fresh.draftErrorMessage || t("draft_failed_msg"),
                color: "red",
              });
            }
          }
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isRunning, project.id, pc.id]);
}

function RhpDraftBar({ pc, project, onUpdate, scope, hintKey }) {
  const { t } = useTranslation("Veft");
  const status = pc.draftStatus || "none";
  const isRunning = status === "generating";
  const isLonglist = scope === "longlistCopy";

  // Buckets the protected fields this scope would touch:
  //  - blocked: HumanApproved (won't be touched even with overwrite=true)
  //  - confirm: HumanWritten or HumanEdited (need explicit overwrite)
  //  - silent : None or AiGenerated (replaced freely)
  function classifyFields(scope) {
    const blocked = [];
    const confirm = [];
    const check = (provenance, hasContent, label) => {
      if (provenance === "humanApproved") blocked.push(label);
      else if ((provenance === "humanWritten" || provenance === "humanEdited") && hasContent)
        confirm.push(label);
    };
    check(pc.executiveSummaryProvenance, !!pc.executiveSummary?.trim(), t("executive_summary"));
    check(pc.annotationsProvenance, (pc.annotations ?? []).length > 0, t("annotations"));
    if (scope === "assessmentCopy") {
      check(pc.personalityNotesProvenance, !!pc.personalityNotes?.trim(), t("personality_notes"));
      for (const s of pc.assessmentSections ?? []) {
        const label = `${t("assessment_sections")} — ${s.heading || "?"}`;
        if (s.provenance === "humanApproved") blocked.push(label);
        else if ((s.provenance === "humanWritten" || s.provenance === "humanEdited") && s.narrative?.trim())
          confirm.push(label);
      }
    }
    return { blocked, confirm };
  }

  async function trigger(overwrite, scope) {
    try {
      // Output language is resolved server-side from the project (or tenant default),
      // not the consultant's UI locale — a German project always produces a German draft.
      await generateRhpDraft(project.id, pc.id, overwrite, scope);
      onUpdate({ ...pc, draftStatus: "generating", draftErrorMessage: null });
    } catch (err) {
      notifications.show({
        title: t("draft_failed"),
        message: err?.detail || err?.message || t("draft_failed_msg"),
        color: "red",
      });
    }
  }

  function handleClick() {
    if (scope === "assessmentCopy") {
      const hasScores = (pc.competencyScores ?? []).some((s) => s.score > 0);
      if (!hasScores) {
        notifications.show({
          title: t("scores_required_title"),
          message: t("scores_required_msg"),
          color: "yellow",
        });
        return;
      }
    }
    const { blocked, confirm } = classifyFields(scope);
    if (blocked.length === 0 && confirm.length === 0) {
      trigger(false, scope);
      return;
    }
    modals.openConfirmModal({
      title: t("draft_overwrite_title"),
      centered: true,
      children: (
        <Stack gap="xs">
          {confirm.length > 0 && (
            <>
              <Text size="sm">{t("draft_overwrite_intro")}</Text>
              <ul style={{ marginTop: 0 }}>
                {confirm.map((c) => <li key={c}><Text size="sm">{c}</Text></li>)}
              </ul>
            </>
          )}
          {blocked.length > 0 && (
            <>
              <Text size="sm" c="green" fw={500}>{t("draft_blocked_approved")}</Text>
              <ul style={{ marginTop: 0 }}>
                {blocked.map((c) => <li key={c}><Text size="sm">{c}</Text></li>)}
              </ul>
            </>
          )}
          <Text size="sm" c="dimmed">{t("draft_overwrite_warning")}</Text>
        </Stack>
      ),
      labels: {
        confirm: confirm.length > 0 ? t("draft_overwrite_confirm") : t("draft_continue"),
        cancel: t("cancel"),
      },
      confirmProps: { color: "blue" },
      onConfirm: () => trigger(confirm.length > 0, scope),
    });
  }

  return (
    <Stack gap={6}>
      <Group justify="space-between" wrap="wrap">
        <Group gap="xs">
          <Button
            size="xs"
            variant={isLonglist ? "light" : "filled"}
            leftSection={<IconRobot size={14} />}
            onClick={handleClick}
            loading={isRunning}
            disabled={isRunning}
          >
            {t(isLonglist ? "draft_longlist_copy" : "draft_assessment_copy")}
          </Button>
          {(status === "generating" || status === "failed") && (
            <Badge size="xs" color={draftStatusColors[status] ?? "gray"} variant="light">
              {t(`draft_status_${status}`)}
            </Badge>
          )}
          {pc.draftLastGeneratedAt && (
            <Text size="xs" c="dimmed">
              {t("draft_last_generated", { date: new Date(pc.draftLastGeneratedAt).toLocaleString() })}
            </Text>
          )}
        </Group>
        {status === "failed" && pc.draftErrorMessage && (
          <Text size="xs" c="red" style={{ maxWidth: "100%" }}>{pc.draftErrorMessage}</Text>
        )}
      </Group>
      <Text size="xs" c="dimmed">{t(hintKey)}</Text>
    </Stack>
  );
}

// ── Referrers ────────────────────────────────────────────────────────────

function ReferrersSection({ pc, project, onUpdate, onNavigate, onClose }) {
  const { t } = useTranslation("Veft");
  const nameFormat = useStore((s) => s.nameFormat);
  const ids = pc.referrerCandidateIds ?? [];
  const [resolved, setResolved] = useState({});
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);

  // Resolve IDs → name/title for display.
  useEffect(() => {
    let cancelled = false;
    const missing = ids.filter((id) => !(id in resolved));
    if (missing.length === 0) return;
    Promise.all(missing.map((id) => getCandidate(id).catch(() => null))).then((cands) => {
      if (cancelled) return;
      setResolved((prev) => {
        const next = { ...prev };
        cands.forEach((c, i) => { if (c) next[missing[i]] = c; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [ids.join(",")]);

  // Debounced name search, excluding the candidate themselves and already-added refs.
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const handle = setTimeout(async () => {
      try {
        const found = await searchCandidates(query.trim());
        const filtered = (found ?? []).filter((c) =>
          c.id !== pc.candidateId && !ids.includes(c.id));
        setResults(filtered.slice(0, 8));
      } catch {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, ids.join(","), pc.candidateId]);

  async function persist(nextIds) {
    setSaving(true);
    try {
      const result = await updateProjectCandidate(project.id, pc.id, { referrerCandidateIds: nextIds });
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("update_failed") });
    } finally {
      setSaving(false);
    }
  }

  function addReferrer(candidate) {
    if (!candidate || ids.includes(candidate.id)) return;
    setResolved((prev) => ({ ...prev, [candidate.id]: candidate }));
    setQuery("");
    setResults([]);
    persist([...ids, candidate.id]);
  }

  function removeReferrer(id) {
    persist(ids.filter((x) => x !== id));
  }

  return (
    <Stack gap="xs">
      <Title order={5}>{t("references")}</Title>
      <Text size="xs" c="dimmed">{t("references_help")}</Text>
      <PillsInput disabled={saving}>
        <Pill.Group>
          {ids.map((id) => {
            const c = resolved[id];
            const label = c ? (formatCandidateName(c, nameFormat) || id) : "…";
            return (
              <Pill
                key={id}
                withRemoveButton
                onRemove={() => removeReferrer(id)}
                style={{ cursor: c ? "pointer" : "default" }}
                onClick={() => { if (c) { onClose(); onNavigate(id); } }}
              >
                {label}
              </Pill>
            );
          })}
          <PillsInput.Field
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder={ids.length === 0 ? t("references_search_placeholder") : ""}
          />
        </Pill.Group>
      </PillsInput>
      {results.length > 0 && (
        <Card withBorder p={4}>
          <Stack gap={2}>
            {results.map((c) => (
              <Anchor key={c.id} component="button" type="button" size="sm"
                onClick={() => addReferrer(c)} style={{ textAlign: "left", padding: "4px 8px" }}>
                {formatCandidateName(c, nameFormat)}
                {candidateHeadline(c, t) && <Text component="span" size="xs" c="dimmed"> — {candidateHeadline(c, t)}</Text>}
              </Anchor>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

// ── Competency × Assessment Matrix ───────────────────────────────────────
//
// One card per competency dimension, laid out as Anforderung (left = the
// requirement the role places on the candidate) vs. Realität (right = the
// candidate's evidence: human-set score + narrative interpretation). The
// score is always consultant-authored; the narrative carries provenance and
// can be approved per-dimension.

function CompetencyAssessmentMatrix({ pc, project, onUpdate, onProjectUpdate }) {
  const { t } = useTranslation("Veft");
  const dimensions = project.competencyDimensions ?? [];
  const scores = pc.competencyScores ?? [];
  const sections = pc.assessmentSections ?? [];

  if (dimensions.length === 0) {
    return (
      <Stack gap="xs">
        <Title order={5}>{t("competency_assessment")}</Title>
        <Text size="xs" c="dimmed">{t("no_competency_dimensions")}</Text>
      </Stack>
    );
  }

  const scoreLookup = Object.fromEntries(scores.map((s) => [s.dimensionName, s.score]));
  const sectionByHeading = Object.fromEntries(
    sections.map((s) => [(s.heading ?? "").trim().toLowerCase(), s]),
  );

  async function handleScoreChange(dimName, value) {
    const newScores = dimensions.map((d) => ({
      dimensionName: d.name,
      score: d.name === dimName ? (value ? parseInt(value) : 0) : (scoreLookup[d.name] ?? 0),
    })).filter((s) => s.score > 0);

    try {
      const result = await updateProjectCandidate(project.id, pc.id, { competencyScores: newScores });
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    }
  }

  async function handleRequirementChange(dimName, description) {
    const updated = dimensions.map((d) =>
      d.name === dimName ? { ...d, description } : d
    );
    try {
      const result = await updateProject(project.id, { competencyDimensions: updated });
      onProjectUpdate?.(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    }
  }

  // For dimensions without a matching section yet, render a placeholder card.
  // Saving a narrative on such a card creates the section (heading = dim.name).
  return (
    <Stack gap="xs">
      <Title order={5}>{t("competency_assessment")}</Title>
      <Text size="xs" c="dimmed">{t("competency_assessment_help")}</Text>
      {dimensions.map((dim) => {
        const section = sectionByHeading[dim.name.trim().toLowerCase()];
        const score = scoreLookup[dim.name];
        return (
          <DimensionCard
            key={dim.name}
            dim={dim}
            section={section}
            score={score}
            onScoreChange={(v) => handleScoreChange(dim.name, v)}
            onRequirementChange={(v) => handleRequirementChange(dim.name, v)}
            pc={pc} project={project} onUpdate={onUpdate}
          />
        );
      })}
    </Stack>
  );
}

function DimensionCard({ dim, section, score, onScoreChange, onRequirementChange, pc, project, onUpdate }) {
  const { t } = useTranslation("Veft");
  const [narrativeDraft, setNarrativeDraft] = useState(normalizeNewlines(section?.narrative));
  const [saving, setSaving] = useState(false);

  // Inline-edit state for the requirement (Anforderung) text.
  const [editingReq, setEditingReq] = useState(false);
  const [reqDraft, setReqDraft] = useState(dim.description ?? "");
  const [reqSaving, setReqSaving] = useState(false);

  // Keep local draft in sync when the upstream pc changes (poll, AI regen, etc.)
  useEffect(() => {
    setNarrativeDraft(normalizeNewlines(section?.narrative));
  }, [section?.id, section?.narrative]);

  useEffect(() => {
    if (!editingReq) setReqDraft(dim.description ?? "");
  }, [dim.description, editingReq]);

  const provenance = section?.provenance || "none";
  const isDirty = narrativeDraft !== normalizeNewlines(section?.narrative);

  async function saveNarrative() {
    if (saving) return;
    if (narrativeDraft === normalizeNewlines(section?.narrative)) return;
    setSaving(true);
    try {
      const next = pc.assessmentSections ?? [];
      let merged;
      if (section) {
        merged = next.map((s) => (s.id === section.id ? { ...s, narrative: narrativeDraft } : s));
      } else {
        merged = [...next, { heading: dim.name, narrative: narrativeDraft }];
      }
      const result = await updateProjectCandidate(project.id, pc.id, { assessmentSections: merged });
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("save_failed") });
    } finally {
      setSaving(false);
    }
  }

  useAutosave({ value: narrativeDraft, isDirty, save: saveNarrative });

  async function saveRequirement() {
    setReqSaving(true);
    try {
      await onRequirementChange(reqDraft.trim());
      setEditingReq(false);
    } finally {
      setReqSaving(false);
    }
  }

  async function handleApprove() {
    if (!section) return;
    try {
      const result = await approveSection(project.id, pc.id, "assessmentSection", section.id);
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("approve_failed") });
    }
  }

  // Both panes sit directly on the card — no per-pane background wash. The only
  // colored surface is the reality textarea's input, tinted by provenance (same
  // language as the Executive Summary / Personality sections). Both header rows
  // share a min-height so the requirement and reality bodies line up even though
  // only the reality side carries the score control.
  const headerRowStyle = { minHeight: 30 };

  return (
    <Card withBorder p="sm">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} size="sm">{dim.name}</Text>
          <Group gap="xs">
            <ProvenancePill value={provenance} />
            {section && (
              <ApproveButton provenance={provenance} onApprove={handleApprove} disabled={saving} />
            )}
          </Group>
        </Group>
        {/* Requirement | Reality: side-by-side on desktop, stacked on a phone (two textareas can't
            share a ~370px drawer width). The vertical divider becomes a horizontal one when stacked. */}
        <Flex align="stretch" gap="md" direction={{ base: "column", sm: "row" }}>
          <Stack gap={4} style={{ flex: 1 }}>
            <Group align="center" style={headerRowStyle}>
              <Text size="xs" fw={500} c="dimmed" tt="uppercase">{t("requirement")}</Text>
            </Group>
            {dim.description?.trim() ? (
              // Read-only here. To edit an existing requirement, go to the
              // project's Settings tab where dimensions are configured.
              <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{dim.description}</Text>
            ) : editingReq ? (
              <Stack gap={4}>
                <Textarea
                  size="sm" autosize minRows={2} maxRows={10}
                  placeholder={t("requirement_placeholder")}
                  value={reqDraft}
                  onChange={(e) => setReqDraft(e.currentTarget.value)}
                  autoFocus
                />
                <Group justify="flex-end" gap="xs">
                  <Button size="compact-xs" variant="subtle"
                    onClick={() => { setEditingReq(false); setReqDraft(""); }}>
                    {t("discard")}
                  </Button>
                  <Button size="compact-xs" onClick={saveRequirement} loading={reqSaving}>
                    {t("save")}
                  </Button>
                </Group>
              </Stack>
            ) : (
              // Fast-path: when no requirement is set, let the consultant fill
              // it inline rather than navigating to the settings tab.
              <Anchor component="button" type="button" size="sm" c="dimmed" fs="italic"
                onClick={() => setEditingReq(true)} style={{ textAlign: "left" }}>
                {t("no_requirement_set")}
              </Anchor>
            )}
          </Stack>
          <Divider orientation="vertical" visibleFrom="sm" />
          <Divider hiddenFrom="sm" />
          <Stack gap={4} style={{ flex: 1.4 }}>
            <Group justify="space-between" align="center" gap="xs" style={headerRowStyle}>
              <Text size="xs" fw={500} c="dimmed" tt="uppercase">{t("reality")}</Text>
              <Group gap={4} wrap="nowrap">
                <Tooltip label={t("score_scale_hint")} withArrow>
                  <Text size="xs" c="dimmed" style={{ cursor: "help" }}>{t("score")}</Text>
                </Tooltip>
                <Select
                  size="xs" w={70}
                  value={score?.toString() ?? ""}
                  data={scoreOptions}
                  onChange={onScoreChange}
                />
              </Group>
            </Group>
            <Textarea
              size="sm" autosize minRows={2} maxRows={10}
              placeholder={t("assessment_narrative_placeholder")}
              value={narrativeDraft}
              onChange={(e) => setNarrativeDraft(e.currentTarget.value)}
              onBlur={() => { if (isDirty) saveNarrative(); }}
              styles={{ input: { backgroundColor: tintFor(provenance) } }}
            />
            {isDirty && (
              <Group justify="flex-end" gap="xs">
                <Button size="compact-xs" variant="subtle" onClick={() => setNarrativeDraft(section?.narrative ?? "")}>
                  {t("discard")}
                </Button>
                <Button size="compact-xs" onClick={saveNarrative} loading={saving}>
                  {t("save")}
                </Button>
              </Group>
            )}
          </Stack>
        </Flex>
      </Stack>
    </Card>
  );
}

// ── Text Section (reusable for Executive Summary / Personality Notes) ────

function TextSection({ labelKey, value, field, provenance, pc, project, onUpdate, placeholderKey, minRows }) {
  const { t } = useTranslation("Veft");
  const label = t(labelKey);
  const [draft, setDraft] = useState(normalizeNewlines(value));
  const [saving, setSaving] = useState(false);
  const isDirty = draft !== normalizeNewlines(value);
  const prov = provenance || "none";

  // Sync local draft when upstream value changes (e.g., AI regen poll). Normalize so a
  // legacy literal "\n" displays as a real line break and doesn't read as unsaved edits.
  useEffect(() => { setDraft(normalizeNewlines(value)); }, [value]);

  async function save(quiet = false) {
    if (saving) return;
    if (draft === normalizeNewlines(value)) return;
    setSaving(true);
    try {
      const result = await updateProjectCandidate(project.id, pc.id, { [field]: draft });
      onUpdate(result);
      if (!quiet) notifications.show({ title: t("saved"), message: t("field_updated", { field: label }), color: "green" });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("field_save_failed", { field: label }) });
    } finally {
      setSaving(false);
    }
  }

  useAutosave({ value: draft, isDirty, save: () => save(true) });

  async function handleApprove() {
    try {
      const result = await approveSection(project.id, pc.id, field);
      onUpdate(result);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("approve_failed") });
    }
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Group gap="xs">
          <Title order={5}>{label}</Title>
          <ProvenancePill value={prov} />
        </Group>
        <ApproveButton provenance={prov} onApprove={handleApprove} disabled={!value?.trim() || isDirty} />
      </Group>
      <Textarea
        autosize minRows={minRows ?? 3} maxRows={20}
        value={draft} onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={() => { if (isDirty) save(true); }}
        placeholder={t(placeholderKey)}
        styles={{ input: { backgroundColor: tintFor(prov) } }}
      />
      {(saving || isDirty) && (
        <Group justify="flex-end" gap="xs">
          {saving && <Text size="xs" c="dimmed">{t("saving")}</Text>}
          {!saving && isDirty && <Text size="xs" c="dimmed">{t("autosave_pending")}</Text>}
          {isDirty && (
            <>
              <Button size="xs" variant="subtle" onClick={() => setDraft(normalizeNewlines(value))}>{t("discard")}</Button>
              <Button size="xs" onClick={() => save(false)} loading={saving}>{t("save")}</Button>
            </>
          )}
        </Group>
      )}
    </Stack>
  );
}
